import { css } from '@emotion/react';
import { Box, Typography } from '@mui/material';
import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from 'src/components/button';
import { PaymentOptionBanner } from 'src/components/payment-option-banner';
import { Section } from 'src/components/section';
import { CustomTooltip } from 'src/components/tooltip';
import { formatAmount } from 'src/utils/format-amount';

import { totalPaymentData } from '../booking-summary/constants';

import { styles } from './styles';

import type { PaymentPlanProps } from './types';

export const getRowStyles = (isDiscount = false, withBorder = false) => {
    if (withBorder) {
        return css(styles.tableRow, styles.tableRowWithBorder);
    }

    if (isDiscount) {
        return css(styles.tableRow, styles.tableRowDiscount);
    }

    return styles.tableRow;
};

export const PaymentPlan: FC<PaymentPlanProps> = ({
    showPaymentOptions = true,
    restStyles,
    onSubmit,
    ctaLabel = '',
    showCta = true,
    loading = false,
    withHeaderStipe = true,
    isEditBookingDetailsDialog = false,
    currency,
    disabled,
    createdPaymentPlan,
    isGapWeekEligible,
    showSantanderCards = false,
}) => {
    const { t } = useTranslation();
    const paymentPlanData = createdPaymentPlan;

    if (!paymentPlanData) return null;

    return (
        <Section header={t('payment_plan_title')} withHeaderStipe={withHeaderStipe}>
            <Box css={css(styles.wrapper, restStyles)}>
                <Box
                    css={[
                        styles.paymentPlanLineItems,
                        isEditBookingDetailsDialog && styles.noBorder,
                    ]}
                >
                    <Box css={css(styles.tableRow, styles.tableRowWithBorder)}>
                        <Box css={styles.firstCell}>
                            <Typography variant="h4" css={styles.paymentPlanColumnTitle}>
                                {t('payment_plan_payment_th')}
                            </Typography>
                        </Box>
                        <Box css={styles.secondCell}>
                            <Typography variant="h4" css={styles.paymentPlanColumnTitle}>
                                {t('payment_plan_due_date_th')}
                            </Typography>
                        </Box>
                        <Box css={styles.thirdCell}>
                            <Typography variant="h4" css={styles.paymentPlanColumnTitle}>
                                {t('payment_plan_amount_th')}
                            </Typography>
                        </Box>
                    </Box>

                    <Box css={css(styles.tableRowWithBorder)}>
                        {paymentPlanData.paymentOptions.map((plan) => (
                            <Box key={plan.name} css={getRowStyles(false, false)}>
                                <Box css={styles.firstCell}>
                                    <Typography variant="body2" css={styles.tableContent}>
                                        {plan.name}
                                    </Typography>
                                </Box>
                                <Box css={styles.secondCell}>
                                    {plan.dueDate ? (
                                        <Typography variant="body2" css={styles.tableContent}>
                                            {plan.dueDate}
                                        </Typography>
                                    ) : null}
                                </Box>
                                <Box css={styles.thirdCell}>
                                    <Typography variant="body2" css={styles.tableContent}>
                                        {formatAmount(plan.price.toFixed(2), currency)}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}

                        {isGapWeekEligible && (
                            <Box css={styles.gapWeekRow}>
                                <Typography variant="body4" css={styles.gapWeekText}>
                                    <Trans
                                        i18nKey="payment_plan_gap_week"
                                        components={{
                                            span: <span css={styles.gapWeekSpan} />,
                                        }}
                                    />
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <Box css={css(styles.tableRowWithBorder, styles.tableRowWithDarkBorder)}>
                        {paymentPlanData.promotion && (
                            <Box>
                                <Box css={getRowStyles(false, false)}>
                                    <Box css={styles.firstCell}>
                                        <Typography variant="body2">
                                            {t('payment_plan_subtotal')}
                                        </Typography>
                                    </Box>
                                    <Box css={styles.thirdCell}>
                                        <Typography variant="body2">
                                            {formatAmount(
                                                paymentPlanData.subtotal.toFixed(2),
                                                currency
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box css={[getRowStyles(false, false), styles.discount]}>
                                    <Box css={styles.firstCell}>
                                        <Typography variant="body2" css={styles.discountLine}>
                                            {t('payment_plan_discount', {
                                                count: paymentPlanData.promotion.number_of_weeks,
                                            })}
                                        </Typography>
                                    </Box>
                                    <Box css={styles.thirdCell}>
                                        <Typography variant="body2" css={styles.discountLine}>
                                            -{' '}
                                            {formatAmount(
                                                paymentPlanData.promotion.amount.toFixed(2),
                                                currency
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                        <Box css={getRowStyles(false, false)}>
                            <Box css={styles.firstCell}>
                                <Typography variant="body2">
                                    {t('payment_plan_total_payable')}
                                </Typography>
                            </Box>
                            <Box css={styles.thirdCell}>
                                <Typography variant="body2">
                                    {formatAmount(paymentPlanData.totalRent.toFixed(2), currency)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        css={css(
                            styles.tableRow,
                            styles.tableRowWithBorder,
                            styles.tableRowWithDarkBorder
                        )}
                    >
                        <Box css={[styles.firstCell, styles.twoRows]}>
                            <Typography variant="h3" css={styles.totalPay}>
                                {t(totalPaymentData.paymentLocale)}
                            </Typography>
                            <CustomTooltip
                                title={
                                    <Typography variant="body4">
                                        {t('payment_plan_total_payable_info')}
                                    </Typography>
                                }
                            />
                        </Box>
                        <Box css={styles.thirdCell}>
                            <Typography
                                variant="h3"
                                css={css(styles.totalPay, styles.totalPayAmount)}
                            >
                                {formatAmount(
                                    paymentPlanData.paymentOptions[0].price.toFixed(2),
                                    currency
                                )}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {!isEditBookingDetailsDialog && (
                    <PaymentOptionBanner
                        showPaymentOptions={showPaymentOptions}
                        showSantanderCards={showSantanderCards}
                    />
                )}

                {showCta && (
                    <Button
                        variant="contained"
                        css={styles.cta}
                        onClick={onSubmit}
                        loading={loading}
                        disabled={disabled}
                        id="submit-button"
                    >
                        {t(ctaLabel)}
                    </Button>
                )}
            </Box>
        </Section>
    );
};
